import type { ReactElement } from 'react';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { Skeleton } from '@ardoq/loader';
import { radius } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  SKELETON_TAG_HEIGHT,
  SKELETON_TITLE_HEIGHT,
} from '../miniOverviewConsts';
import type { SkeletonVariant } from '../types';

type SkeletonDetailRow = {
  key: string;
  labelWidth: string;
  valueWidth: string;
};

const SKELETON_DETAIL_ROWS: SkeletonDetailRow[] = [
  { key: 'detail-1', labelWidth: '40%', valueWidth: '30%' },
  { key: 'detail-2', labelWidth: '32%', valueWidth: '24%' },
  { key: 'detail-3', labelWidth: '45%', valueWidth: '18%' },
];

type LoadingSkeletonProps = {
  variant: SkeletonVariant;
};

export const LoadingSkeleton = ({
  variant,
}: LoadingSkeletonProps): ReactElement => (
  <Stack gap="none">
    <Box paddingBottom="medium">
      <Stack gap="small">
        <Skeleton
          variant={variant}
          width="65%"
          height={SKELETON_TITLE_HEIGHT}
        />
        <Skeleton variant={variant} width="45%" />
      </Stack>
    </Box>
    <Box borderWidthTop={1} borderColor="borderSubtle" paddingTop="medium">
      <Stack gap="medium">
        <Stack gap="small">
          <Stack gap="xxsmall">
            <Skeleton variant={variant} />
            <Skeleton variant={variant} />
            <Skeleton variant={variant} width="70%" />
          </Stack>
          <Skeleton
            variant={variant}
            width="35%"
            height={SKELETON_TAG_HEIGHT}
            borderRadius={radius.full}
          />
        </Stack>
        <Stack gap="none">
          {SKELETON_DETAIL_ROWS.map(({ key, labelWidth, valueWidth }) => (
            <FlexBox
              key={key}
              gap="medium"
              width="full"
              justify="space-between"
              align="center"
              paddingY="small"
              borderWidthTop={1}
              borderColor="borderSubtle"
            >
              <Skeleton variant={variant} width={labelWidth} />
              <Skeleton variant={variant} width={valueWidth} />
            </FlexBox>
          ))}
        </Stack>
      </Stack>
    </Box>
  </Stack>
);

type LoadingCardProps = {
  i18nLocale: I18nLocale;
  variant: SkeletonVariant;
};

export const LoadingCard = ({
  i18nLocale,
  variant,
}: LoadingCardProps): ReactElement => (
  <Box
    role="status"
    aria-label={t(
      i18nLocale,
      'componentOverview:miniOverview.loading',
      'Loading component'
    )}
    aria-busy
  >
    <LoadingSkeleton variant={variant} />
  </Box>
);
